import { useState } from 'react';
import { CheckCircle2, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useBuyerAuth } from '@/context/BuyerAuthContext';
import { BuyerAuthDialog } from '@/components/auction/BuyerAuthDialog';
import { submitOffer, apiError } from '@/api/auction';
import { INPUT_CLS, LABEL_CLS } from '@/components/brand/BrandKit';
import { at } from '@/i18n/auctionI18n';

function OfferSignInPrompt({ lang, onAuth }) {
  return (
    <>
      <DialogTitle className="font-heading font-black text-2xl text-[#121212]">{at(lang, 'signInToOffer')}</DialogTitle>
      <DialogDescription className="font-body text-sm text-[#666666]">{at(lang, 'signInSub')}</DialogDescription>
      <div className="mt-5 space-y-3">
        <button type="button" data-testid="offer-register-btn" onClick={onAuth} className="btn-primary w-full">{at(lang, 'registerToBid')}</button>
        <button type="button" data-testid="offer-signin-btn" onClick={onAuth} className="btn-outline w-full">{at(lang, 'haveAccount')}</button>
      </div>
    </>
  );
}

function OfferSuccess({ lang, buyer, done, onClose }) {
  return (
    <div className="text-center py-2">
      <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
      <DialogTitle className="mt-4 font-heading font-black text-2xl text-[#121212]">{at(lang, 'offerSubmitted')}</DialogTitle>
      <p className="mt-2 font-body text-sm text-[#666666]">{at(lang, 'thankYou', buyer.full_name, (done.amount_aed || 0).toLocaleString(), done.vehicle_label)}</p>
      <button type="button" data-testid="offer-done-close" onClick={onClose} className="btn-primary mt-6">{at(lang, 'done')}</button>
    </div>
  );
}

function OfferForm({ lang, vehicle, amount, setAmount, message, setMessage, err, busy, onSubmit }) {
  return (
    <>
      <DialogTitle className="font-heading font-black text-2xl text-[#121212]">{at(lang, 'submitSealed')}</DialogTitle>
      <DialogDescription className="font-body text-sm text-[#666666]">{vehicle.year} {vehicle.make} {vehicle.model} · {at(lang, 'lotWord')} {vehicle.lot_no}</DialogDescription>
      <div className="mt-4 space-y-4">
        <div>
          <Label className={LABEL_CLS}>{at(lang, 'yourOfferAed')}</Label>
          <Input data-testid="offer-amount" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={at(lang, 'offerPh')} className={INPUT_CLS} />
        </div>
        <div>
          <Label className={LABEL_CLS}>{at(lang, 'messageOptional')}</Label>
          <Textarea data-testid="offer-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder={at(lang, 'messagePh')} className={`${INPUT_CLS} !h-auto min-h-[80px]`} />
        </div>
        <p className="flex items-start gap-2 font-body text-[11px] text-[#888]"><Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {at(lang, 'sealedNote')}</p>
        {err && <p data-testid="offer-error" role="alert" className="bg-red-50 border border-red-200 text-red-600 font-body text-sm p-3 rounded-lg">{err}</p>}
        <button type="button" data-testid="offer-submit" onClick={onSubmit} disabled={busy} className="btn-primary w-full disabled:opacity-50">{busy ? at(lang, 'submitting') : at(lang, 'submitOfferBtn')}</button>
      </div>
    </>
  );
}

export function OfferDialog({ vehicle, className = 'btn-primary w-full', label, lang = 'en' }) {
  const { buyer } = useBuyerAuth();
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [message, setMessage] = useState('');
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const isAr = lang === 'ar';
  const closed = ['sold', 'released', 'unavailable'].includes(vehicle.availability_status);
  const btnLabel = label || at(lang, 'submitOffer');

  const submit = async () => {
    setErr('');
    const amt = Number(amount);
    if (!amt || amt <= 0) { setErr(at(lang, 'invalidAmount')); return; }
    setBusy(true);
    try {
      const res = await submitOffer(vehicle.id, { amount_aed: Math.round(amt), message });
      setDone(res);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const reset = () => { setDone(null); setAmount(''); setMessage(''); setErr(''); };
  const openAuth = () => { setOpen(false); setAuthOpen(true); };

  const renderContent = () => {
    if (!buyer) return <OfferSignInPrompt lang={lang} onAuth={openAuth} />;
    if (done) return <OfferSuccess lang={lang} buyer={buyer} done={done} onClose={() => setOpen(false)} />;
    return <OfferForm lang={lang} vehicle={vehicle} amount={amount} setAmount={setAmount} message={message} setMessage={setMessage} err={err} busy={busy} onSubmit={submit} />;
  };

  return (
    <>
      <button type="button" data-testid="open-offer-dialog" disabled={closed} onClick={() => { reset(); setOpen(true); }} className={`${className} disabled:opacity-50`}>
        {closed ? at(lang, 'offersClosed') : btnLabel}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="max-w-md bg-white">
          {renderContent()}
        </DialogContent>
      </Dialog>

      <BuyerAuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={() => setOpen(true)} lang={lang} />
    </>
  );
}
